import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'
import type { CommercialHealth } from './useRelationships'
import type { CommitmentSyncSummary } from './useCommitments'
import type { CommercialSignal } from './useCommercialSignals'

export type EvidenceSourceState = 'ok' | 'not_owner' | 'consent_required' | 'not_configured' | 'timeout' | 'degraded'
export type EvidenceAssociationState = 'suggested' | 'ambiguous'

export interface EvidenceTimelineItem {
  id: string
  resourceId?: string
  type: string
  source: string
  title: string
  date: string
  detail: string | null
  personId: string | null
  personName?: string | null
  url: string | null
  status?: string
  synchronization?: CommitmentSyncSummary
  timing?: 'past' | 'upcoming' | null
  direction?: 'enviado' | 'recibido' | 'borrador'
  participants?: string[]
  conversation?: boolean
  organizer?: string | null
  attendees?: string[]
  responseStatus?: string | null
  location?: string
  fileType?: string | null
  evidenceState: 'confirmed' | 'legacy_unverified'
  ownership: 'verified' | 'legacy_unverified' | 'sales'
}

export interface EvidenceCandidate {
  candidateId: string
  resourceId: string
  type: 'correo' | 'reunion' | 'documento' | 'chat_teams'
  source: string
  title: string
  date: string | null
  url: string | null
  associationState: EvidenceAssociationState
  reason: string
  personId: string | null
  direction?: 'enviado' | 'recibido' | 'borrador'
  conversationId?: string | null
  participants?: string[]
  organizer?: string | null
  attendees?: string[]
  responseStatus?: string | null
  timing?: 'past' | 'upcoming'
  location?: string
  fileType?: string | null
  chatType?: string | null
}

export interface RelationshipEvidence {
  rulesVersion: 'evidence-v1'
  generatedAt: string
  ownership: {
    state: 'verified' | 'not_owner'
    currentOwnerId: string
    relationshipResponsibleId: string | null
    message: string
  }
  summary: {
    rulesVersion: string
    health: CommercialHealth
    lastValidInteraction: { source: string; id: string; date: string; label: string } | null
    nextMeeting: { title: string; date: string; url: string | null } | null
    nextCommitment: { id: string; title: string; date: string | null } | null
    keyPeople: Array<{ id: string; title: string; role: string | null }>
    blockers: string[]
    nextAction: string
    evidenceQuality: {
      completeness: number
      available: number
      total: number
      sourceCoverage: number
      verifiedTimelineItems: number
      dimensions: Record<string, boolean>
    }
  }
  sourceStates: Record<string, EvidenceSourceState>
  timeline: EvidenceTimelineItem[]
  legacyUnverified: EvidenceTimelineItem[]
  candidates: EvidenceCandidate[]
  contacts: Array<{ personId: string; personName: string; salesEmail: string | null; state: 'linked' | 'suggested' | 'not_found'; evidence: string }>
  todo: Array<{
    commitmentId: string
    title: string
    salesState: string
    dueDate: string | null
    mirror: { state: string | null; listName: string | null; divergence: boolean; bindingStatus: string } | null
    synchronization: { status: string; verifiedAt?: string | null }
  }>
  teams: { state: EvidenceSourceState; count: number; note: string }
  transcripts: { state: EvidenceSourceState; count: number; meetings: string[]; note: string }
  commercialSignals: CommercialSignal[]
}

type Status = 'idle' | 'loading' | 'ready' | 'error'

export function useRelationshipEvidence(id: string | undefined, enabled = true) {
  const { gate, makeClient } = useApiClient()
  const [data, setData] = useState<RelationshipEvidence | null>(null)
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [decidingId, setDecidingId] = useState<string | null>(null)

  const reload = useCallback(() => setAttempt((value) => value + 1), [])

  useEffect(() => {
    if (!id || !enabled || gate !== 'ready') return
    const client = makeClient()
    if (!client) return
    const controller = new AbortController()
    setStatus('loading')
    setError(null)
    client.get<RelationshipEvidence>(`/api/relationships/${id}/evidence`, { signal: controller.signal })
      .then((response) => { setData(response); setStatus('ready') })
      .catch((reason: ApiError | Error) => {
        if (controller.signal.aborted) return
        setError(reason); setStatus('error')
      })
    return () => controller.abort()
  }, [id, enabled, gate, makeClient, attempt])

  const decide = useCallback(async (candidateId: string, decision: 'confirm' | 'discard') => {
    const client = makeClient()
    if (!client || !id) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
    setDecidingId(candidateId)
    try {
      await client.postOnce(`/api/relationships/${id}/evidence`, { candidateId, decision })
      reload()
    } finally { setDecidingId(null) }
  }, [id, makeClient, reload])

  return { data, status, error, reload, decide, decidingId }
}
